import React, { useState } from 'react';

import { useMutation } from '@betterment/js-runtime';
import { Button, FormikField as Field, Input, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { trackClick } from '../../features/track';
import { urlBuilder } from '../../Shared/urlBuilder';
import { InviteParams } from '../data';

interface SendInviteFormProps {
  trackingModule: string;
}

const inviteValidationSchema = Yup.object().shape({
  receivingUserEmail: Yup.string()
    .required('Please enter an email address')
    .email('Please enter a valid email address'),
});

const SendInviteForm: React.FC<React.PropsWithChildren<SendInviteFormProps>> = ({ trackingModule }) => {
  const [hasInviteSent, setInviteSent] = useState(false);
  const [postInvite] = useMutation<InviteParams>(urlBuilder.postPersonalReferralInvites());

  const onSubmitFlashMessage = () => {
    setInviteSent(true);

    setTimeout(() => {
      setInviteSent(false);
    }, 3000);
  };

  const onSubmitHandler = async (values: InviteParams, formik: FormikHelpers<InviteParams>) => {
    const response = await postInvite(values);
    if (!response.errors) {
      formik.setSubmitting(false);
      formik.resetForm();
      onSubmitFlashMessage();
      return;
    }

    formik.setErrors(response.errors.messages);
  };

  return (
    <Formik
      initialValues={{ receivingUserEmail: '' }}
      onSubmit={onSubmitHandler}
      validationSchema={inviteValidationSchema}
      enableReinitialize={true}
    >
      {({ errors, isSubmitting }) => (
        <Form className={css({ display: 'grid', gap: 'space.200' })}>
          <Field name="receivingUserEmail">
            <Field.Label>Email your friend</Field.Label>
            <Field.Control>
              <Input placeholder="Email address" />
            </Field.Control>
            <Field.ErrorMessage />
          </Field>
          <Button
            css={{ width: '100%' }}
            disabled={isSubmitting || hasInviteSent}
            type="submit"
            aria-label="Send email invitation"
            onClick={() => trackClick({ module: trackingModule, name: 'SendEmailSubmission' })}
          >
            Invite
          </Button>
          {hasInviteSent && !errors.receivingUserEmail && (
            <Text
              color="text.success"
              css={{
                fontSize: 'fontSize.50',
              }}
            >
              Invite sent
            </Text>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default SendInviteForm;
